import { AxiosError } from "axios";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import useSWR from "swr";
import {
  GetTradeHistory200Response,
  InstrumentTypeResponse,
  OptionTypeResponse,
  TradeTypeResponse,
} from "../../../codegen-api";
import { AuthContext } from "../../../contexts/AuthContext";
import {
  tradeHistorySignature,
  useTradeSyncTarget,
} from "../../../services/tradeSync";
import { WebsocketContext } from "../../../contexts/WebsocketAuthContext";
import { APIEndpointEnum } from "../../../enums/endpoint";
import { authApi } from "../../../services/api/apiFetcher";
import { sortTrades } from "../../../utils/trade";
import { AssetResponse } from "../../../utils/asset";
import { isStaleApiCredentialError } from "../../../utils/authError";
import { pollingInterval } from "../../../services/api/pollingInterval";
import {
  clearScheduledRevalidations,
  scheduleRevalidations,
} from "../../../utils/revalidation";
import {
  getRealtimeAccount,
  getRealtimeEventId,
  parseRealtimeMessage,
  shouldRunRealtimeRevalidation,
} from "../../../utils/realtimeRevalidation";
import { forEachFrame } from "../../wss/parseFrame";

export const useTradeHistory = (
  startTime: number,
  asset?: AssetResponse,
  tradeTypes?: TradeTypeResponse[],
  agg?: boolean,
  endTime?: number,
  limit?: number,
  optionType?: OptionTypeResponse,
  instrumentType?: InstrumentTypeResponse,
  offset?: number,
  enabled = true
) => {
  // Get API Key
  const { apiKey, apiSecret, account, deleteAuthInfo } = useContext(AuthContext);
  const { authenticated, triggerSubscribe, triggerUnsubscribe, lastMessages } =
    useContext(WebsocketContext);
  const fetcher = useMemo(
    () => authApi(apiKey, apiSecret),
    [apiKey, apiSecret]
  );
  const processedRealtimeEvents = useRef<Set<string>>(new Set());
  const lastRealtimeSnapshots = useRef<Map<string, string>>(new Map());
  const accountKey = account?.toLowerCase();
  const revalidationTimersRef = useRef<number[]>([]);
  const lastRevalidationRef = useRef(0);

  const swrKey = enabled && apiKey && apiSecret && accountKey
    ? [
        APIEndpointEnum.TRADES,
        accountKey,
        apiKey,
        apiSecret,
        startTime,
        asset,
        tradeTypes,
        agg,
        endTime,
        limit,
        offset,
        optionType,
        instrumentType,
      ]
    : undefined;

  const swr = useSWR<GetTradeHistory200Response, AxiosError>(
    swrKey,
    async () => {
      const { data } = await (
        await fetcher.getTradeHistory(
          startTime ?? 0,
          asset,
          endTime,
          tradeTypes,
          instrumentType,
          optionType,
          limit,
          agg,
          offset,
          "created_timestamp",
          "DESC"
        )
      )();
      // Handle both array response (new API) and object response (old API)
      const tradeHistory = Array.isArray(data) ? data : data.trade_history;
      const count = Array.isArray(data) ? String(data.length) : data.count;
      return {
        count,
        trade_history: tradeHistory?.sort(sortTrades),
      };
    },
    {
      dedupingInterval: 10000,
      refreshInterval: pollingInterval[APIEndpointEnum.TRADES],
      revalidateOnFocus: false,
      // Keep previous data only while an account is selected — clears the laggy
      // trade history on disconnect (see useOrderController for the rationale).
      keepPreviousData: Boolean(accountKey),
      revalidateIfStale: true,
      revalidateOnMount: true,
      shouldRetryOnError: false,
      errorRetryCount: 0,
      errorRetryInterval: 1000,
      focusThrottleInterval: 10000,
      onError: (e) => {
        const error = e as AxiosError;
        const responseData = error.response?.data as any;
        const isInvalidToken =
          responseData?.trim?.() === "INVALID_TOKEN" ||
          responseData?.error === "INVALID_TOKEN";
        if (isInvalidToken || isStaleApiCredentialError(e)) {
          deleteAuthInfo("api");
          swr.mutate(undefined, { revalidate: false });
        }
      },
    }
  );

  const accountRealtimeChannels = useMemo(
    () =>
      enabled && account
        ? [
            `trade-history:${account.toLowerCase()}`,
            `position-history:${account.toLowerCase()}`,
          ]
        : [],
    [account, enabled]
  );

  useEffect(() => {
    if (!enabled || !authenticated || accountRealtimeChannels.length === 0) {
      return undefined;
    }
    accountRealtimeChannels.forEach((channel) => {
      triggerSubscribe("subscribe", [channel]);
    });
    // Release on unmount, account change, disable or de-auth (M1).
    return () => triggerUnsubscribe(accountRealtimeChannels);
  }, [accountRealtimeChannels, authenticated, enabled, triggerSubscribe, triggerUnsubscribe]);

  // Converge on the trade list after a confirmed trade: a fill adds a row, and
  // the set of trade ids is the only evidence this list needs.
  useTradeSyncTarget({
    // The id must describe the QUERY, not just the account. This hook is mounted
    // by TradeHistoryTab, PortfolioFundingHistoryTab, MobileTradeHistoryTab and
    // MobileFundingHistoryTab, which fetch different rows — and the funding ones
    // cannot converge on a perp trade at all, since their signature is a set of
    // funding trade ids. Sharing one id meant they were guaranteed to exhaust
    // and write STALE over the real trade list's status, and one unmount cleared
    // a sibling's badge mid-sync.
    targetId: `trade-history:${accountKey || "anon"}:${JSON.stringify([
      startTime,
      asset,
      tradeTypes,
      agg,
      endTime,
      limit,
      offset,
      optionType,
      instrumentType,
    ])}`,
    // Armed on exactly the condition the FETCH uses. Gating on `accountKey`
    // alone armed convergence while the SWR key was still null — every refetch
    // resolved undefined, the signature never changed, and the run burned its
    // whole budget to publish a STALE that meant nothing. This state is real and
    // routine: a connected wallet before the API key exists.
    enabled: Boolean(swrKey),
    current: () => swr.data,
    refetch: () => swr.mutate(),
    signature: tradeHistorySignature,
  });

  const revalidateTradeHistorySoon = useCallback(
    (includeImmediate = true) => {
      if (!enabled) {
        return;
      }
      scheduleRevalidations(
        swr.mutate,
        revalidationTimersRef,
        lastRevalidationRef,
        {
          delays: [750],
          throttleMs: 10000,
          includeImmediate,
        }
      );
    },
    [enabled, swr.mutate]
  );

  useEffect(
    () => () => clearScheduledRevalidations(revalidationTimersRef),
    []
  );

  useEffect(() => {
    if (!enabled || !lastMessages || accountRealtimeChannels.length === 0) {
      return;
    }

    let shouldRevalidate = false;
    const channelSet = new Set(
      accountRealtimeChannels.map((channel) =>
        channel.split(":")[0].toLowerCase()
      )
    );
    const normalizedAccount = account?.toLowerCase();

    forEachFrame(lastMessages, "useTradeHistory", (lastMessage) => {
      const parsed = parseRealtimeMessage(lastMessage.data);
      const channel = parsed?.channel?.toLowerCase();
      const isBackendAccountEvent =
        normalizedAccount &&
        getRealtimeAccount(parsed) === normalizedAccount &&
        !!channel &&
        channelSet.has(channel) &&
        parsed?.data !== undefined;

      if (!isBackendAccountEvent) {
        return;
      }

      const snapshotSignature = JSON.stringify(parsed?.data || null);
      const snapshotKey = `${channel}:${normalizedAccount}:trade-history`;
      const previousSignature = lastRealtimeSnapshots.current.get(snapshotKey);
      lastRealtimeSnapshots.current.set(snapshotKey, snapshotSignature);
      if (
        previousSignature !== undefined &&
        previousSignature === snapshotSignature
      ) {
        return;
      }

      const eventId = `${snapshotKey}:${getRealtimeEventId(parsed, channel)}`;
      if (processedRealtimeEvents.current.has(eventId)) {
        return;
      }

      processedRealtimeEvents.current.add(eventId);
      if (processedRealtimeEvents.current.size > 200) {
        const oldest = processedRealtimeEvents.current.values().next().value;
        if (oldest) processedRealtimeEvents.current.delete(oldest);
      }
      shouldRevalidate = true;
    });

    if (shouldRevalidate) {
      const gateKey = [
        APIEndpointEnum.TRADES,
        accountKey,
        asset || "",
        instrumentType || "",
      ].join(":");
      if (shouldRunRealtimeRevalidation(gateKey, 10000)) {
        revalidateTradeHistorySoon(false);
      }
    }
  }, [
    account,
    accountKey,
    accountRealtimeChannels,
    asset,
    enabled,
    instrumentType,
    lastMessages,
    revalidateTradeHistorySoon,
  ]);

  return swr;
};
